import { Fragment } from 'react';

import {
  Badge,
  Button,
  Checkbox,
  Flex,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Portal,
  Text,
  Tooltip,
} from '@chakra-ui/react';

import { ColumnDef, CellContext, createColumnHelper, Row, Table } from '@tanstack/react-table';

import { format, parseISO } from 'date-fns';

import {
  CalendarOutline,
  DEFAULT_DATE_FORMAT,
  IndividualStatus,
  ClipboardCloseOutline as ClipboardClose,
  MoreCircleOutline as MoreCircle,
} from '@aslanpay/portal-common-lib';

import {
  EmployeeActions,
  EmployeeBulkActions,
  EmployeeRow,
} from 'app/components/UI/Table/Table.types';

import {
  EmployeeColumns,
  EmployeeActionsIds,
  defaultEmployeeActionsValues,
} from 'app/constants/table';
import { Status } from 'app/constants/individual';

export function getEmployeesColumns(actions: EmployeeActions) {
  const columnHelper = createColumnHelper<EmployeeRow>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<EmployeeRow, any>[] = Object.keys(EmployeeColumns).map((field) => {
    return columnHelper.accessor(field as keyof EmployeeRow, {
      cell: (info) => {
        switch (field as keyof EmployeeRow) {
          case 'email':
            return <Text color="brand.purple">{info.getValue() as string}</Text>;
          case 'status':
            return (
              <Flex align="center" gap="8px">
                <Badge colorScheme={`badge.${Status[info.getValue() as IndividualStatus]?.color}`}>
                  {Status[info.getValue() as IndividualStatus]?.value}
                </Badge>
                {info.row.original.onUnpaidLeave && (
                  <Tooltip
                    hasArrow
                    shouldWrapChildren
                    arrowPadding={0}
                    placement="bottom-end"
                    label="This employee is on accrual pause."
                  >
                    <ClipboardClose boxSize="20px" color="brand.unpaid" />
                  </Tooltip>
                )}
                {info.row.original.terminationDate &&
                  ['LEAVING', 'RESTRICTED'].includes(info.row.original.status) && (
                    <Tooltip
                      hasArrow
                      shouldWrapChildren
                      arrowPadding={0}
                      placement="bottom-end"
                      label={`This employee is leaving employment on ${format(
                        parseISO(info.row.original.terminationDate),
                        DEFAULT_DATE_FORMAT
                      )}.`}
                    >
                      <CalendarOutline boxSize="20px" color="brand.leaving" />
                    </Tooltip>
                  )}
              </Flex>
            );
          case 'payCalendarName':
            return <Text>{(info.getValue() as string) || '-'}</Text>;
          default:
            return info.getValue();
        }
      },

      header: EmployeeColumns[field as keyof typeof EmployeeColumns],
      enableSorting: field !== 'companyName',
      meta: {
        getCellContext: (context: CellContext<EmployeeRow, unknown>) => {
          return {
            onClick: () => actions.profile && actions.profile([context.row.index]),
          };
        },
      },
    });
  });

  columns.unshift({
    id: 'select',
    enableSorting: false,
    header: ({ table }: { table: Table<EmployeeRow> }) => (
      <Checkbox
        {...{
          isChecked: table.getIsAllPageRowsSelected(),
          isIndeterminate: table.getIsSomePageRowsSelected(),
          onChange: table.getToggleAllPageRowsSelectedHandler(),
        }}
      />
    ),
    cell: ({ row }: { row: Row<EmployeeRow> }) => (
      <Checkbox
        {...{
          isChecked: row.getIsSelected(),
          isIndeterminate: row.getIsSomeSelected(),
          onChange: row.getToggleSelectedHandler(),
        }}
      />
    ),
    meta: {
      customStyles: { justifyContent: 'center' },
      pin: 'left',
      withBackground: true,
    },
  });

  columns.push(
    columnHelper.display({
      id: 'options',
      meta: {
        getCellContext: (context: CellContext<EmployeeRow, unknown>) => {
          if (context.column.id === 'options') return { style: { padding: 0, textAlign: 'end' } };
          return undefined;
        },
        customStyles: { justifyContent: 'center' },
        pin: 'right',
      },
      cell: (info) => (
        <Menu autoSelect={false} strategy="fixed">
          <MenuButton
            as={IconButton}
            aria-label="Options"
            icon={<MoreCircle boxSize="20px" />}
            variant="menu"
          />
          <Portal>
            <MenuList>
              {Object.keys(actions).flatMap((action, index) => {
                const onActionClick = actions[action as keyof typeof actions];

                if (onActionClick) {
                  const actionValue = defaultEmployeeActionsValues.find(
                    (value) => value.id === action
                  );

                  let isDisabled = false;

                  // TODO: Need to cleanup this logic

                  if (
                    actionValue?.id === EmployeeActionsIds.INVITE &&
                    info.row.original.status !== 'CREATED' &&
                    info.row.original.status !== 'INVITED'
                  ) {
                    return [];
                  }

                  if (
                    actionValue?.id === EmployeeActionsIds.INVITE &&
                    info.row.original.status === 'INVITED'
                  ) {
                    isDisabled = true;
                  }

                  if (actionValue?.id === EmployeeActionsIds.SET_UNPAID) {
                    if (info.row.original.onUnpaidLeave) return [];

                    if (
                      !['CREATED', 'INVITED', 'ACTIVE', 'LEAVING'].includes(
                        info.row.original.status
                      )
                    ) {
                      isDisabled = true;
                    }
                  }

                  if (
                    actionValue?.id === EmployeeActionsIds.REMOVE_UNPAID &&
                    !info.row.original.onUnpaidLeave
                  ) {
                    return [];
                  }

                  if (
                    actionValue?.id === EmployeeActionsIds.TERMINATE &&
                    (info.row.original.status === 'LEAVING' ||
                      info.row.original.status === 'LEFT' ||
                      info.row.original.status === 'RESTRICTED')
                  ) {
                    return [];
                  }

                  if (
                    actionValue?.id === EmployeeActionsIds.EDIT_TERMINATE &&
                    !['LEAVING', 'RESTRICTED'].includes(info.row.original.status)
                  ) {
                    return [];
                  }

                  return (
                    <Fragment key={action}>
                      {index > 0 && <MenuDivider key={`${info.row.index}${action}cenas`} />}
                      <MenuItem
                        key={action}
                        color="brand.black"
                        onClick={() => onActionClick([info.row.index])}
                        icon={<Icon as={actionValue?.icon} boxSize="14px" color="brand.darkGray" />}
                        isDisabled={isDisabled}
                      >
                        {actionValue?.value}
                      </MenuItem>
                    </Fragment>
                  );
                }
                return [];
              })}
            </MenuList>
          </Portal>
        </Menu>
      ),
    })
  );

  return columns;
}

export function getEmployeeBulkActionsValues<TData extends object>(
  actions: EmployeeBulkActions,
  selectedRows: Row<TData>[]
) {
  const isInviteDisabled = !selectedRows.some(
    (row) => (row.original as EmployeeRow).status === 'CREATED'
  );

  const bulkActions = {
    ...actions,
    invite: { ...(actions as EmployeeBulkActions).invite, isDisabled: isInviteDisabled },
  };

  const bulkActionsElements = Object.keys(bulkActions).map((bulkAction) => {
    const actionValue = defaultEmployeeActionsValues.find((value) => value.id === bulkAction);

    return (
      <Button
        key={bulkAction}
        size="bulk"
        variant="bulk"
        onClick={() =>
          bulkActions[bulkAction as keyof EmployeeBulkActions]?.action(
            selectedRows.map((row) => row.index)
          )
        }
        isDisabled={bulkActions[bulkAction as keyof EmployeeBulkActions]?.isDisabled}
        _disabled={{ opacity: 1, cursor: 'not-allowed' }}
      >
        <Icon as={actionValue?.icon} boxSize="20px" />
        <Text>{actionValue?.value}</Text>
      </Button>
    );
  });

  return { label: 'employee', bulkActions: bulkActionsElements };
}
